import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Billing > Envoy dashboard. Gated server-side by envoy_finance:view (reads) /
// envoy_finance:manage (mark paid / undo). The GET runs the reconcile sweep
// server-side, so a refetch IS the refresh mechanism.
// `range` = { start, end } (YYYY-MM-DD, Central days, inclusive) or null for
// all time.
const rangeParams = (range) =>
  range?.start && range?.end ? { start: range.start, end: range.end } : {};

export const useEnvoyDashboard = (range, opts = {}) =>
  useQuery(
    ['envoy-dashboard', range?.start || 'all', range?.end || 'all'],
    async () => {
      const res = await axios.get('/billing/envoy/dashboard', {
        params: rangeParams(range),
      });
      return res.data;
    },
    {
      refetchOnWindowFocus: true,
      staleTime: 30000,
      keepPreviousData: true,
      ...opts,
    },
  );

// Lead-level correlation analytics (the Connections tab). Heavier compute,
// so it caches longer than the ledger.
export const useEnvoyConnections = (opts = {}) =>
  useQuery(
    ['envoy-connections'],
    async () => {
      const res = await axios.get('/billing/envoy/connections');
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      keepPreviousData: true,
      ...opts,
    },
  );

// Full row set for the CSV (plain async, not a hook - export button handler).
export const fetchEnvoyExport = async (range) => {
  const res = await axios.get('/billing/envoy/export', {
    params: rangeParams(range),
  });
  return res.data;
};

export const useMarkCommissionPaid = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ commissionId, leg, paidAt, reference }) => {
      const res = await axios.post(
        `/billing/envoy/commissions/${commissionId}/mark-paid`,
        { leg, paidAt, reference },
      );
      return res.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['envoy-dashboard']),
    },
  );
};

export const useUnmarkCommissionPaid = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ commissionId, leg }) => {
      const res = await axios.post(
        `/billing/envoy/commissions/${commissionId}/unmark-paid`,
        { leg },
      );
      return res.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['envoy-dashboard']),
    },
  );
};

// Recoup: Envoy returned a $250 we paid for a client who withdrew unpaid.
export const useMarkCommissionRecouped = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ commissionId, recoupedAt, reference }) => {
      const res = await axios.post(
        `/billing/envoy/commissions/${commissionId}/mark-recouped`,
        { recoupedAt, reference },
      );
      return res.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['envoy-dashboard']),
    },
  );
};

export const useUnmarkCommissionRecouped = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ commissionId }) => {
      const res = await axios.post(
        `/billing/envoy/commissions/${commissionId}/unmark-recouped`,
      );
      return res.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['envoy-dashboard']),
    },
  );
};
